import axios from 'axios';
import utils from 'axios/lib/utils';
import dispatchRequest from 'axios/lib/core/dispatchRequest';
import bind from 'axios/lib/helpers/bind';
import mergeConfig from 'axios/lib/core/mergeConfig';
import { preparCombineParams, combineParamSerializer } from './util';

/**
 * 为了兼容业务端调用方式，实现最常用的promise方法
 *
 * @class LPPromise
 */
class LPPromise {
  constructor(promise, context) {
    this.promise = promise;
    this.context = context;
    this.type = 'lp-promise';
  }

  then(fulfilled, rejected) {
    return this.promise.then((config) => {
      return this.context.doRequest(config).then(fulfilled, rejected);
    });
  }

  catch(error) {
    return this.promise.then((config) => {
      return this.context.doRequest(config).catch(error);
    });
  }

  finally(final) {
    return this.promise.then((config) => {
      return this.context.doRequest(config).finally(final);
    });
  }
}

axios.Axios.prototype.doRequest = function doRequest(config) {
  const chain = [dispatchRequest, undefined];
  let promise = Promise.resolve(config);

  this.interceptors.response.forEach(function pushResponseInterceptors(
    interceptor
  ) {
    chain.push(interceptor.fulfilled, interceptor.rejected);
  });

  while (chain.length) {
    promise = promise.then(chain.shift(), chain.shift());
  }

  return promise;
};

// 注入新的合并请求方法
axios.Axios.prototype.combineRequest = function combineRequest(conf, ...args) {
  let config = {};
  if (typeof conf === 'string') {
    config = args[0] || {};
    config.url = conf;
  } else {
    config = conf || {};
  }

  config = mergeConfig(this.defaults, config);

  // 设置配置的方法
  if (config.method) {
    config.method = config.method.toLowerCase();
  } else if (this.defaults.method) {
    config.method = this.defaults.method.toLowerCase();
  } else {
    config.method = 'get';
  }

  const chain = [];
  let promise = Promise.resolve(config);

  this.interceptors.request.forEach(function unshiftRequestInterceptors(
    interceptor
  ) {
    chain.unshift(interceptor.fulfilled, interceptor.rejected);
  });

  while (chain.length) {
    promise = promise.then(chain.shift(), chain.shift());
  }

  return new LPPromise(promise, this);
};

// 合并要发送的请求
axios.Axios.prototype.combine = function combine(...args) {
  // 生成合并后的请求, 默认强制post
  let rewriteConfig = {
    method: 'post',
    params: {}, // reset params
  };
  const requestList = [...args];
  let config = {};
  let configReolve;

  const configPromise = new Promise((resolve) => {
    configReolve = resolve;
  });

  requestList.forEach((curr, i) => {
    // 如果不是fake promise 暂时跳过不处理
    if (!curr || curr.type !== 'lp-promise') {
      return;
    }
    curr.promise.then((preConfig) => {
      // 使用用户自定义参数处理逻辑
      if (
        preConfig.combine &&
        typeof preConfig.combine.paramsProcesser === 'function'
      ) {
        rewriteConfig = preConfig.combine.paramsProcesser(
          preConfig,
          rewriteConfig,
          i,
          requestList
        );
      } else {
        // 预定义合并逻辑
        rewriteConfig = preparCombineParams(preConfig, rewriteConfig);
      }
      config = mergeConfig(config, preConfig);
      if (i === requestList.length - 1) {
        // config.data = rewriteConfig.data;
        const originUrl = config.url;
        // 处理数据
        if (
          config.combine &&
          (config.combine.isRemoveQuery === undefined ||
            config.combine.isRemoveQuery)
        ) {
          config.params = {};
        }

        if (
          preConfig.combine &&
          typeof config.combine.paramsSerializer === 'function'
        ) {
          // 这里没有重新合并，会直接覆盖需要注意
          rewriteConfig = config.combine.paramsSerializer(rewriteConfig);
        } else {
          rewriteConfig = combineParamSerializer(rewriteConfig);
        }
        // 合并配置数据
        config = mergeConfig(config, rewriteConfig);
        // 如果配置了combine url 参数则使用配置参数，否则退回到最后合并的url
        config.url = (config.combine && config.combine.url) || originUrl;
        // 是否保留query查询语句
        if (config.combine && config.combine.isRemoveQuery) {
          config.params = {};
        }
        configReolve(config);
      }
    });
  });
  // 调用通用发送方法
  return this.doRequest(configPromise);
};

// 添加支持的默认方法
utils.forEach(
  ['combineDelete', 'combineGet', 'combineHead', 'combineOptions'],
  function forEachMethodNoData(method) {
    const requestMethod = method.replace('combine', '').toLowerCase();
    /* eslint func-names:0 */
    axios.Axios.prototype[method] = function (url, config) {
      return this.combineRequest(
        mergeConfig(config || {}, {
          requestMethod,
          url,
          data: (config || {}).data,
        })
      );
    };
  }
);

utils.forEach(
  ['combinePost', 'combinePut', 'combinePatch'],
  function forEachMethodWithData(method) {
    const requestMethod = method.replace('combine', '').toLowerCase();
    // eslint-disable-next-line func-names
    axios.Axios.prototype[method] = function (url, data, config) {
      return this.combineRequest(
        mergeConfig(config || {}, {
          requestMethod,
          url,
          data,
        })
      );
    };
  }
);

// 修复this绑定bug
utils.extend(axios, {
  combine: bind(axios.Axios.prototype.combine, axios),
  combineRequest: bind(axios.Axios.prototype.combineRequest, axios),
  doRequest: axios.Axios.prototype.doRequest,
});

// 绑定快捷支持方法，如果内置的话则不需要绑定
utils.forEach(
  [
    'combineDelete',
    'combineGet',
    'combineHead',
    'combineOptions',
    'combinePost',
    'combinePut',
    'combinePatch',
  ],
  function forEachMethodWithData(method) {
    utils.extend(axios, {
      [method]: bind(axios.Axios.prototype[method], axios),
    });
  }
);

export { axios };

export default axios;
